// Serviço de autenticação: comunica com a API externa de auth (login,
// logout, verificação de token) e persiste os dados da sessão no localStorage.
//
// Endpoints: POST /auth/login, POST /auth/logout, GET /auth/verify

import authApi from '../../../services/authApi';
import logger from '../../../utils/logger';

const STORAGE_KEYS = Object.freeze({
  TOKEN: 'token',
  USER: 'user',
  OAB: 'oab'
});

// tabela TipoInscricao (API de Protocolo) — fornecida pelo usuário, dono do
// catálogo. "Transferência" existe pras 2 categorias com IDs diferentes (2 e 7),
// por isso a chave combina descrição (normalizada) + tipoCategoria, não só a
// descrição sozinha.
const TIPO_INSCRICAO_REMETENTE_POR_CHAVE = {
  'originária|ADVOGADO(A)': '1',
  'transferência|ESTAGIÁRIO(A)': '2',
  'suplementar|ADVOGADO(A)': '3',
  'estagiário|ESTAGIÁRIO(A)': '5',
  'transferência|ADVOGADO(A)': '7',
  'definitiva|SOCIEDADE': '8'
};

const normalizarDescricao = (v) => String(v || '').trim().toLowerCase();

// Resolve o ID (tabela TipoInscricao) a partir do texto que o login devolve em
// oab.tipoInscricaoAtual (ex: "Originária", "Transferência"...) — nunca inventa:
// se o texto não bater com nenhuma combinação conhecida (descrição + categoria),
// não resolve nada (undefined = campo omitido, mesmo comportamento de antes da
// API de login expor esse dado).
const resolverTipoInscricaoRemetente = (oab) => {
  const chave = `${normalizarDescricao(oab?.tipoInscricaoAtual)}|${oab?.tipoCategoria}`;
  return TIPO_INSCRICAO_REMETENTE_POR_CHAVE[chave];
};

export const authService = {

  async login(cpfCnpj, password) {
    try {
      logger.log('🔐 Realizando login...');

      const response = await authApi.post('/auth/login', {
        documento: cpfCnpj,
        senha: password
      });

      const data = response.data;

      if (!data.success) {
        return {
          success: false,
          error: data.message || 'Usuário ou senha inválidos'
        };
      }

      const { token, usuario, oab } = data.data;

      // tipoInscricaoRemetente (API de Protocolo, ID numérico) resolvido a partir
      // de oab.tipoInscricaoAtual (texto) + oab.tipoCategoria — pra não precisar
      // mexer em quem já lê oabData?.tipoInscricaoRemetente
      // (protocoloRequerimentoService.js).
      const oabComRemetente = oab ? { ...oab, tipoInscricaoRemetente: resolverTipoInscricaoRemetente(oab) } : oab;

      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(usuario));
      localStorage.setItem(STORAGE_KEYS.OAB, JSON.stringify(oabComRemetente));

      logger.log('✅ Login realizado com sucesso');

      // Formato esperado pelo AuthContext
      return {
        success: true,
        token,
        usuario,
        oab: oabComRemetente
      };

    } catch (error) {
      logger.error('❌ Erro no login:', error);

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro ao realizar login'
      );
    }
  },

  async logout() {
    try {
      await authApi.post('/auth/logout');
      logger.log('🚪 Logout realizado com sucesso');
    } catch (error) {
      // API usa JWT stateless: mesmo que a chamada falhe, o logout local deve acontecer
      logger.log('⚠️ Erro ao notificar logout da API:', error.message);
    } finally {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.OAB);
    }

    return { success: true };
  },

  /**
   * Valida o token JWT junto à API. Diferente de isAuthenticated(), que só
   * checa se existe token local, este método confirma com o servidor.
   */
  async verifyToken() {
    const response = await authApi.get('/auth/verify');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Token inválido');
    }

    return response.data;
  },

  // Verifica apenas se existe token local, sem validar junto à API
  isAuthenticated() {
    return !!localStorage.getItem(STORAGE_KEYS.TOKEN);
  },

  getCurrentUser() {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    if (!user) return null;

    try {
      return JSON.parse(user);
    } catch (error) {
      logger.error('❌ Erro ao parsear usuário do localStorage:', error);
      return null;
    }
  },

  getOABData() {
    const oab = localStorage.getItem(STORAGE_KEYS.OAB);
    if (!oab) return null;

    try {
      return JSON.parse(oab);
    } catch (error) {
      logger.error('❌ Erro ao parsear dados OAB:', error);
      return null;
    }
  },

  getToken() {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }
};
